import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import EmptyState from "./EmptyState";
import TooltipUI from "./Tooltip";
import InfoIcon from "./InfoIcon";

const EngagementScoreChart = ({ data, height = 360 }) => {
  // Check if we have actual data
  if (!data || data.length === 0) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle flex flex-col" style={{ height }}>
        <h3 className="text-sm font-normal text-black-0 mb-4">Engagement score</h3>
        <EmptyState
          variant="no-data"
          title="No engagement scores available"
          description="There's no engagement score data for the selected period. Try selecting a different date range."
          actionButton={null}
        />
      </div>
    );
  }

  // Format date for display (e.g., "Jan 1" or "Feb 15")
  const formatDate = (dateStr) => {
    try {
      // Handle "Jun 1 2025, Monday" format
      const datePart = dateStr.split(',')[0]; // Get "Jun 1 2025"
      const date = new Date(datePart);
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', dateStr);
        return dateStr.split(',')[0] || dateStr; // Fallback to original
      }
      
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
    } catch (error) {
      console.warn('Error formatting date:', dateStr, error);
      return dateStr.split(',')[0] || dateStr; // Fallback
    }
  };

  // Calculate average score
  const calculateAverage = () => {
    if (!data || data.length === 0) return 0;
    const sum = data.reduce((total, item) => total + item.score, 0);
    return Math.round(sum / data.length);
  };

  // Get engagement level based on score
  const getEngagementLevel = (score) => {
    if (score >= 80) return "Very high";
    if (score >= 60) return "High";
    if (score >= 40) return "Medium";
    if (score >= 20) return "Low";
    return "Very low";
  };

  // Get engagement level badge color (same as Activity column)
  const getEngagementBadgeColor = (level) => {
    switch (level.toLowerCase()) {
      case 'high':
      case 'very high':
        return 'bg-[#DAFBEB] text-black-0';
      case 'medium':
        return 'bg-[#FBF0DA] text-black-0';
      case 'low':
      case 'very low':
        return 'bg-[#FBDADA] text-black-0';
      default:
        return 'bg-grey-20 text-grey-60';
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      try {
        // Handle "Jun 1 2025, Monday" format
        const datePart = label.split(',')[0]; // Get "Jun 1 2025"
        const date = new Date(datePart);
        
        let formattedDate = label; // Fallback to original
        if (!isNaN(date.getTime())) {
          formattedDate = date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric'
      });
        }
      
      return (
        <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
          <p className="text-sm font-medium">{formattedDate}</p>
          <p className="text-sm text-grey-60">
            Score: <span className="font-medium">{payload[0].value}%</span>
          </p>
          <p className="text-sm text-grey-60">
            Messages: <span className="font-medium">{payload[0].payload.messageCount || 0}</span>
          </p>
        </div>
      );
      } catch (error) {
        console.warn('Error in tooltip formatting:', error);
        return null;
      }
    }
    return null;
  };

  return (
    <div className="bg-white-f p-5 pl-0 rounded-lg border border-grey-20 shadow-subtle flex flex-col" style={{ height }}>
      <div className="pl-5 mb-4 flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-normal text-black-0">Engagement score</h3>
          <TooltipUI
            content="Daily engagement score (0–100) based on participation and activity patterns in the selected channels."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <div className="flex items-center gap-2">
          <p className="font-medium text-[18px] text-black-0">
            {calculateAverage()}%
          </p>
          <span className={`px-2 py-1 rounded-md text-xs font-normal ${getEngagementBadgeColor(getEngagementLevel(calculateAverage()))}`}>
            {getEngagementLevel(calculateAverage())}
          </span>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate} 
              tick={{ fontSize: 12 }}
              tickMargin={10}
              stroke="#ABABBA"
            />
            <YAxis 
              domain={[0, 100]} 
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 12 }}
              tickMargin={10}
              stroke="#ABABBA"
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="linear"
              dataKey="score"
              stroke="#31048B"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 5, fill: "#31048B" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EngagementScoreChart; 